import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarDays,
  FilePlus2,
  Send,
  UploadCloud,
  UserMinus,
  UserRoundCheck,
} from 'lucide-react';

import { Alert } from '@/components/ui/Alert';
import { AttachmentTile } from '@/components/ui/AttachmentTile';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { DatePicker } from '@/components/ui/DatePicker';
import { QueryError } from '@/components/ui/QueryError';
import { Select } from '@/components/ui/Select';
import { Skeleton } from '@/components/ui/Skeleton';
import { Textarea } from '@/components/ui/Textarea';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { cn } from '@/lib/utils';
import type { DailyReportAttachment, DailyReportClass, TeachingMethod } from '@/types/dailyReport';
import { dailyReportsApi } from '../api/dailyReports.api';
import { teacherApi } from '../api/teacher.api';
import {
  ATTACHMENT_ACCEPT,
  MAX_ATTACHMENT_MB,
  MAX_ATTACHMENTS,
  PARTICIPATION_BANDS,
  TEACHING_METHOD_LABEL,
  TEACHING_METHODS,
  participationBand,
} from '../dailyReport/constants';
import { periodRange } from '../components/timetable/timetableCopy';
import { formatInstituteDate } from '../utils';

/** The longest a topic may run, matching `daily_reports.content`. */
const TOPIC_LIMIT = 2000;
/** And the three shorter columns beside it. */
const LINE_LIMIT = 500;

/**
 * Writing up one class, on its own screen.
 *
 * **The class, subject and day are the address, not a choice made twice.** They
 * arrive in the query string, so the form can be linked to, reloaded and shared
 * — and the three pickers at the top change that address rather than holding a
 * second copy of it in state that could drift from what was loaded.
 *
 * Which classes are offered at all comes from the day's own timetable, so this
 * screen cannot write a report against a class the teacher does not take that
 * day. The server checks the same thing again; this only keeps the form honest.
 *
 * Attendance is shown and never edited here. It is a separate record taken on a
 * separate screen, and a second place to change it would be a second answer.
 */
export function TeacherDailyReportFormPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const date = params.get('date') ?? '';
  const classSectionUuid = params.get('class') ?? '';
  const subjectUuid = params.get('subject') ?? '';
  const isViewMode = params.get('mode') === 'view';

  const { data, isLoading, error, refetch } = useApiQuery(
    () => dailyReportsApi.day(date || undefined),
    [date],
  );

  /** The registers for the day, for the read-only attendance strip. */
  const resolvedDate = data?.date ?? '';
  const attendance = useApiQuery(
    () => (resolvedDate ? teacherApi.overview(resolvedDate) : Promise.resolve(null)),
    [resolvedDate],
  );

  const classes = useMemo(() => data?.classes ?? [], [data]);

  /**
   * The class-and-subject being written up.
   *
   * Falls back to the first class of the day when the address names one the
   * teacher does not take — a link followed on the wrong day, or a date changed
   * under a class that is not on it.
   */
  const entry = useMemo<DailyReportClass | null>(() => {
    if (classes.length === 0) return null;
    const exact = classes.find(
      (item) => item.classSectionUuid === classSectionUuid && item.subject.uuid === subjectUuid,
    );

    return exact ?? classes.find((item) => item.classSectionUuid === classSectionUuid) ?? classes[0];
  }, [classes, classSectionUuid, subjectUuid]);

  const report = entry?.report ?? null;
  const isNotYetTaught = Boolean(entry && !entry.hasBeenTaught);
  /**
   * A class still to come cannot be written up. One already written up too early
   * stays open, but only so it can be withdrawn: the server refuses anything else.
   */
  const isReadOnly = isViewMode || (isNotYetTaught && !report);

  // --- the draft -----------------------------------------------------------

  const [topic, setTopic] = useState('');
  const [homework, setHomework] = useState('');
  const [resources, setResources] = useState('');
  const [remarks, setRemarks] = useState('');
  const [methods, setMethods] = useState<TeachingMethod[]>([]);
  const [percent, setPercent] = useState<number | null>(null);
  const [shareWithParents, setShareWithParents] = useState(false);
  const [kept, setKept] = useState<DailyReportAttachment[]>([]);
  const [added, setAdded] = useState<File[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const fileInput = useRef<HTMLInputElement>(null);

  /**
   * Seeded from what is stored, every time the class or the day changes.
   *
   * Keyed on the entry's identity rather than on the object, so a refetch that
   * returns the same report does not wipe what is being typed over it.
   */
  const entryKey = entry ? `${data?.date}:${entry.classSectionUuid}:${entry.subject.uuid}` : '';
  useEffect(() => {
    setTopic(report?.content ?? '');
    setHomework(report?.homeworkGiven ?? '');
    setResources(report?.resourcesUsed ?? '');
    setRemarks(report?.remarks ?? '');
    setMethods(report?.methods ?? []);
    setPercent(report?.participationPercent ?? null);
    setShareWithParents(report?.sharedWithParents ?? false);
    setKept(report?.attachments ?? []);
    setAdded([]);
    // `report` is read through `entryKey`: the effect must run when the class or
    // the day changes, never on every refetch of the same one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryKey]);

  const classOptions = useMemo(() => {
    const byUuid = new Map(classes.map((item) => [item.classSectionUuid, item.displayName]));

    return [...byUuid].map(([value, label]) => ({ value, label }));
  }, [classes]);

  /**
   * A teacher of one subject has nothing to choose, so the field is left out;
   * the report is still for that subject, read from the address as before.
   */
  const teachesSeveralSubjects = useMemo(
    () => new Set(classes.map((item) => item.subject.uuid)).size > 1,
    [classes],
  );

  /** Only the subjects this teacher takes in the chosen class, on this day. */
  const subjectOptions = useMemo(
    () =>
      classes
        .filter((item) => item.classSectionUuid === (entry?.classSectionUuid ?? ''))
        .map((item) => ({ value: item.subject.uuid, label: item.subject.name })),
    [classes, entry],
  );

  const register = attendance.data?.sections.find(
    (section) => section.classSectionUuid === entry?.classSectionUuid,
  );
  const isRegisterTaken = Boolean(register?.attendance.isMarked);

  /** Moves the address rather than holding a second copy of it in state. */
  function goTo(next: { class?: string; subject?: string; date?: string }) {
    const updated = new URLSearchParams(params);
    if (next.class !== undefined) {
      updated.set('class', next.class);
      // The subject is chosen within the class, so a new class clears it and
      // the fallback above picks that class's first subject.
      updated.delete('subject');
    }
    if (next.subject !== undefined) updated.set('subject', next.subject);
    if (next.date !== undefined) updated.set('date', next.date);
    setParams(updated, { replace: true });
  }

  function toggleMethod(method: TeachingMethod) {
    setMethods((current) =>
      current.includes(method)
        ? current.filter((value) => value !== method)
        : [...current, method],
    );
  }

  /**
   * Takes the files the browser handed over, refusing what the server would.
   *
   * Checked here for the message's sake only — the size, the type and the count
   * are all decided again on the server, which is the one that matters (§10).
   */
  function acceptFiles(incoming: FileList | null) {
    if (!incoming || incoming.length === 0) return;

    const room = MAX_ATTACHMENTS - kept.length - added.length;
    if (room <= 0) {
      toast.error('That is as many files as a report can hold', `The limit is ${MAX_ATTACHMENTS}.`);
      return;
    }

    const chosen = [...incoming];
    const tooBig = chosen.filter((file) => file.size > MAX_ATTACHMENT_MB * 1024 * 1024);
    const usable = chosen.filter((file) => file.size <= MAX_ATTACHMENT_MB * 1024 * 1024);

    if (tooBig.length > 0) {
      toast.error(
        tooBig.length === 1 ? 'That file is too large' : 'Some files are too large',
        `Each one must be under ${MAX_ATTACHMENT_MB} MB.`,
      );
    }

    if (usable.length > room) {
      toast.error(
        'Only some of those were added',
        `A report holds ${MAX_ATTACHMENTS} files, so there was room for ${room}.`,
      );
    }

    setAdded((current) => [...current, ...usable.slice(0, room)]);
  }

  const isDirty =
    topic.trim() !== (report?.content ?? '') ||
    homework.trim() !== (report?.homeworkGiven ?? '') ||
    resources.trim() !== (report?.resourcesUsed ?? '') ||
    remarks.trim() !== (report?.remarks ?? '') ||
    percent !== (report?.participationPercent ?? null) ||
    shareWithParents !== (report?.sharedWithParents ?? false) ||
    added.length > 0 ||
    kept.length !== (report?.attachments.length ?? 0) ||
    methods.length !== (report?.methods.length ?? 0) ||
    methods.some((method) => !report?.methods.includes(method));

  const isWithdrawing = Boolean(report) && topic.trim().length === 0;

  async function save() {
    if (!entry || !data) return;
    setIsSaving(true);

    try {
      const saved = await dailyReportsApi.save(
        {
          classSectionUuid: entry.classSectionUuid,
          subjectUuid: entry.subject.uuid,
          reportDate: data.date,
          content: topic.trim(),
          // Null rather than left off when empty: the server reads an absent
          // field as "leave it as stored", so clearing a box has to be said.
          homeworkGiven: homework.trim() || null,
          resourcesUsed: resources.trim() || null,
          remarks: remarks.trim() || null,
          participationPercent: percent,
          methods,
          sharedWithParents: shareWithParents,
          keepAttachmentUuids: kept.map((file) => file.uuid),
        },
        added,
      );

      toast.success(
        saved ? 'Report saved' : 'Report withdrawn',
        `${entry.displayName} · ${entry.subject.name}`,
      );
      navigate('/teacher/daily-report');
    } catch (caught) {
      // The server's messages name the reason — a subject this class does not
      // teach, a date outside the session — so they are shown verbatim.
      toast.error(
        'Could not save this report',
        caught instanceof ApiRequestError ? caught.message : 'Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  if (error) {
    return (
      <div className="flex flex-col gap-5">
        <QueryError title="Could not load your day" error={error} onRetry={refetch} />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-5">
        <Skeleton className="h-24 w-full rounded-card" />
        <Skeleton className="h-16 w-full rounded-card" />
        <Skeleton className="h-96 w-full rounded-card" />
      </div>
    );
  }

  const selectedBand = participationBand(percent);

  /** The day being written up, in words — "today" only when it is. */
  const dayPhrase = !data || data.isToday ? 'today' : `on ${formatInstituteDate(data.date)}`;
  const fileCount = kept.length + added.length;

  return (
    <div className="flex flex-col gap-5">
      {/* --- 1. PAGE HEADER --------------------------------------------------- */}
      <header className="card flex flex-col gap-3.5 rounded-[16px] border border-line bg-surface p-4 shadow-2xs sm:flex-row sm:items-center sm:justify-between sm:p-5">
        <div className="flex min-w-0 items-center gap-3.5">
          <span
            className="grid h-11 w-11 shrink-0 place-items-center rounded-[12px] border border-violet-500/25 bg-violet-500/15 text-violet-600 dark:text-violet-400 sm:h-12 sm:w-12"
            aria-hidden
          >
            <FilePlus2 className="h-5 w-5 sm:h-6 sm:w-6" />
          </span>
          <div className="min-w-0">
            <h1 className="text-xl font-bold tracking-tight text-ink sm:text-2xl">
              {isReadOnly ? 'Daily Report' : report ? 'Edit Daily Report' : 'Add Daily Report'}
            </h1>
            <p className="mt-0.5 text-xs font-medium text-ink-3 sm:text-sm">
              Record the class activities, topics covered, homework and other details {dayPhrase}.
            </p>
          </div>
        </div>

        <Button
          variant="secondary"
          leftIcon={<ArrowLeft className="h-4 w-4" />}
          onClick={() => navigate('/teacher/daily-report')}
          className="w-full sm:w-auto"
        >
          Back to Daily Report
        </Button>
      </header>

      {data?.holiday ? (
        <Alert tone="info" title={data.holiday.name}>
          The institute is closed on this day, so there is nothing to report. Choose another date
          from the Daily Report screen.
        </Alert>
      ) : !entry ? (
        <Alert tone="info" title="No classes on this day">
          You are not timetabled to teach on this date, so there is no class to write up.
        </Alert>
      ) : (
        <>
          {isNotYetTaught && !isViewMode && (
            <Alert tone="info" title="This class has not been taught yet">
              {report
                ? 'A report was saved before this class took place. You can withdraw it by clearing the topic, and write it up again after the class.'
                : `You can write up ${entry.displayName} · ${entry.subject.name} once the period has ended.`}
            </Alert>
          )}

          {/* --- 2. WHAT IS BEING WRITTEN UP ------------------------------- */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Select
              label="Class"
              options={classOptions}
              value={entry.classSectionUuid}
              disabled={isViewMode}
              onChange={(event) => goTo({ class: event.target.value })}
            />
            {teachesSeveralSubjects && (
              <Select
                label="Subject"
                options={subjectOptions}
                value={entry.subject.uuid}
                disabled={isViewMode}
                onChange={(event) => goTo({ subject: event.target.value })}
              />
            )}
            <DatePicker
              label="Date"
              value={data?.date ?? ''}
              disabled={isViewMode}
              onChange={(event) => goTo({ date: event.target.value })}
            />

            {/* Read-only, and deliberately. One report covers every period this
                class takes this subject in on the day — see the table's own
                migration for why it is not per period. */}
            <div>
              <span className="lbl mb-1.5 block">Period</span>
              <div className="flex min-h-[2.5rem] flex-wrap items-center gap-1.5 rounded-field border border-line bg-surface-2 px-3 py-2">
                {entry.periods.map((period) => (
                  <span key={period.uuid} className="pill bg-accent-soft text-accent">
                    <CalendarDays className="h-3 w-3" aria-hidden />
                    {period.periodNumber} ({periodRange(period.startTime, period.endTime)})
                  </span>
                ))}
              </div>
              <p className="mt-1 text-2xs font-medium text-ink-3">
                One report covers every period of this subject {dayPhrase}.
              </p>
            </div>
          </div>

          {/* --- 3. TOPICS & METHOD ---------------------------------------- */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <section className="card flex flex-col rounded-[16px] border border-line bg-surface p-5 shadow-2xs">
              <Textarea
                label="Topics Covered"
                rows={4}
                maxLength={TOPIC_LIMIT}
                required
                value={topic}
                disabled={isSaving || isReadOnly}
                onChange={(event) => setTopic(event.target.value)}
                placeholder={`Syllabus covered, activities, tests set for ${entry.displayName}…`}
                hint={
                  isReadOnly
                    ? undefined
                    : 'Enter the key topics discussed in the class. Clearing this withdraws the report.'
                }
              />
              <p className="mt-1 text-right text-2xs font-semibold tabular-nums text-ink-3">
                {topic.length}/{TOPIC_LIMIT}
              </p>
            </section>

            <section className="card flex flex-col rounded-[16px] border border-line bg-surface p-5 shadow-2xs">
              <fieldset disabled={isSaving || isReadOnly} className="min-w-0">
                <legend className="lbl mb-3">Teaching Method / Activities</legend>
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  {TEACHING_METHODS.map((method) => (
                    <Checkbox
                      key={method}
                      label={TEACHING_METHOD_LABEL[method]}
                      checked={methods.includes(method)}
                      onChange={() => toggleMethod(method)}
                    />
                  ))}
                </div>
              </fieldset>
            </section>
          </div>

          {/* --- 4. HOMEWORK & RESOURCES ----------------------------------- */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <section className="card flex flex-col rounded-[16px] border border-line bg-surface p-5 shadow-2xs">
              <Textarea
                label="Homework Given"
                rows={3}
                maxLength={LINE_LIMIT}
                value={homework}
                disabled={isSaving || isReadOnly}
                onChange={(event) => setHomework(event.target.value)}
                placeholder="Complete Worksheet 1, Read Chapter 1"
                hint={isReadOnly ? undefined : 'Mention the homework or assignments given to students.'}
              />
              <p className="mt-1 text-right text-2xs font-semibold tabular-nums text-ink-3">
                {homework.length}/{LINE_LIMIT}
              </p>
            </section>

            <section className="card flex flex-col rounded-[16px] border border-line bg-surface p-5 shadow-2xs">
              <Textarea
                label="Resources Used (Optional)"
                rows={3}
                maxLength={LINE_LIMIT}
                value={resources}
                disabled={isSaving || isReadOnly}
                onChange={(event) => setResources(event.target.value)}
                placeholder="Textbook, Smart Board, Worksheets"
                hint={isReadOnly ? undefined : 'E.g. Textbook, Smart Board, Worksheets, Online Resources.'}
              />
              <p className="mt-1 text-right text-2xs font-semibold tabular-nums text-ink-3">
                {resources.length}/{LINE_LIMIT}
              </p>
            </section>
          </div>

          {/* --- 5. ATTENDANCE & PARTICIPATION ----------------------------- */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <section className="card flex flex-col rounded-[16px] border border-line bg-surface p-5 shadow-2xs">
              <div className="flex items-baseline justify-between gap-2">
                <h2 className="lbl">Class Attendance</h2>
                <span className="text-2xs font-semibold text-ink-3">
                  Taken on the Attendance screen
                </span>
              </div>

              {attendance.isLoading ? (
                <Skeleton className="mt-3 h-20 w-full rounded-field" />
              ) : !isRegisterTaken ? (
                /* A register nobody took is not a class that was all absent. */
                <p className="mt-4 text-sm font-medium text-ink-3">
                  No register has been taken for {entry.displayName} {dayPhrase}, so there is nothing to
                  show here. Nothing on this form depends on it.
                </p>
              ) : (
                <div className="mt-3 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                  <AttendanceTile
                    tone="emerald"
                    icon={<UserRoundCheck className="h-4 w-4" />}
                    label="Present incl. late"
                    value={(register?.attendance.present ?? 0) + (register?.attendance.late ?? 0)}
                  />
                  <AttendanceTile
                    tone="rose"
                    icon={<UserMinus className="h-4 w-4" />}
                    label="Absent"
                    value={register?.attendance.absent ?? 0}
                  />
                  <AttendanceTile
                    tone="violet"
                    icon={<CalendarDays className="h-4 w-4" />}
                    label="Leave"
                    value={register?.attendance.leave ?? 0}
                  />
                  <AttendanceTile
                    label="Total Students"
                    value={register?.totalStudents ?? 0}
                  />
                </div>
              )}
            </section>

            <section className="card flex flex-col rounded-[16px] border border-line bg-surface p-5 shadow-2xs">
              <fieldset disabled={isSaving || isReadOnly} className="min-w-0">
                <legend className="lbl mb-3">Student Participation</legend>

                {/* A radiogroup, not four checkboxes: exactly one band applies,
                    and arrow keys must move between them as a screen reader
                    user expects. */}
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-4">
                  {PARTICIPATION_BANDS.map((band) => {
                    const isSelected = selectedBand?.value === band.value;

                    return (
                      <label
                        key={band.value}
                        className={cn(
                          'flex cursor-pointer flex-col gap-1 rounded-field border p-3 transition-colors',
                          'focus-within:ring-2 focus-within:ring-accent-line',
                          isSelected
                            ? 'border-accent bg-accent-soft'
                            : 'border-line bg-surface-2 hover:border-accent',
                          (isSaving || isReadOnly) && 'cursor-not-allowed opacity-70',
                        )}
                      >
                        <span className="flex items-center gap-2">
                          <input
                            type="radio"
                            name="participation"
                            className="h-4 w-4 shrink-0 accent-[var(--accent)]"
                            checked={isSelected}
                            onChange={() => setPercent(band.percent)}
                          />
                          <span className="text-sm font-bold text-ink">{band.label}</span>
                        </span>
                        <span className="text-2xs font-medium leading-snug text-ink-3">
                          {band.description}
                        </span>
                      </label>
                    );
                  })}
                </div>

                {percent !== null && !isReadOnly && (
                  <button
                    type="button"
                    onClick={() => setPercent(null)}
                    className="mt-2.5 self-start text-2xs font-bold text-ink-3 underline transition-colors hover:text-accent"
                  >
                    Clear — this class was not rated
                  </button>
                )}
              </fieldset>
            </section>
          </div>

          {/* --- 6. REMARKS ------------------------------------------------- */}
          <section className="card flex flex-col rounded-[16px] border border-line bg-surface p-5 shadow-2xs">
            <Textarea
              label="Remarks (Optional)"
              rows={2}
              maxLength={LINE_LIMIT}
              value={remarks}
              disabled={isSaving || isReadOnly}
              onChange={(event) => setRemarks(event.target.value)}
              placeholder="How the lesson went, and anything to follow up."
              hint={isReadOnly ? undefined : 'Add any additional notes, observations or follow-up actions.'}
            />
            <p className="mt-1 text-right text-2xs font-semibold tabular-nums text-ink-3">
              {remarks.length}/{LINE_LIMIT}
            </p>
          </section>

          {/* --- 7. ATTACHMENTS --------------------------------------------- */}
          <section className="card flex flex-col rounded-[16px] border border-line bg-surface p-5 shadow-2xs">
            <div className="flex items-baseline justify-between gap-2">
              <h2 className="lbl">Attach Files (Optional)</h2>
              <span className="text-2xs font-semibold tabular-nums text-ink-3">
                {fileCount}/{MAX_ATTACHMENTS}
              </span>
            </div>

            <div className="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-2">
              {!isReadOnly && (
                <div
                  onDragOver={(event) => {
                    event.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={(event) => {
                    event.preventDefault();
                    setIsDragging(false);
                    acceptFiles(event.dataTransfer.files);
                  }}
                  className={cn(
                    'flex flex-col items-center justify-center gap-1.5 rounded-field border border-dashed p-6 text-center transition-colors',
                    isDragging ? 'border-accent bg-accent-soft' : 'border-line bg-surface-2',
                  )}
                >
                  <UploadCloud className="h-6 w-6 text-ink-3" aria-hidden />
                  <p className="text-sm font-medium text-ink-2">
                    Drag and drop files here or{' '}
                    <button
                      type="button"
                      onClick={() => fileInput.current?.click()}
                      className="font-bold text-accent underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line"
                    >
                      click to upload
                    </button>
                  </p>
                  <p className="text-2xs font-medium text-ink-3">
                    PDF, DOC, DOCX, JPG, PNG — up to {MAX_ATTACHMENT_MB} MB each
                  </p>

                  <input
                    ref={fileInput}
                    type="file"
                    multiple
                    accept={ATTACHMENT_ACCEPT}
                    className="sr-only"
                    aria-label="Choose files to attach to this report"
                    onChange={(event) => {
                      acceptFiles(event.target.files);
                      // Cleared so choosing the same file twice still fires.
                      event.target.value = '';
                    }}
                  />
                </div>
              )}

              {/* Room above for the tiles' corner buttons. */}
              <ul className="flex flex-wrap gap-4 pt-2">
                {/* Already saved — previewed by fetching it behind the same read
                    check as the report. */}
                {kept.map((file) => (
                  <li key={file.uuid}>
                    <AttachmentTile
                      name={file.fileName}
                      size={file.fileSize}
                      type={file.mimeType}
                      path={dailyReportsApi.attachmentUrl(file.uuid)}
                      onRemove={
                        isReadOnly
                          ? undefined
                          : () => setKept((current) => current.filter((f) => f.uuid !== file.uuid))
                      }
                    />
                  </li>
                ))}

                {/* Chosen just now, still in the browser. */}
                {added.map((file, index) => (
                  <li key={`${file.name}-${index}`}>
                    <AttachmentTile
                      name={file.name}
                      size={file.size}
                      file={file}
                      onRemove={() =>
                        setAdded((current) => current.filter((_, at) => at !== index))
                      }
                    />
                  </li>
                ))}

                {fileCount === 0 && (
                  <li className="text-sm font-medium text-ink-3">
                    Nothing attached to this report.
                  </li>
                )}
              </ul>
            </div>
          </section>

          {/* --- 8. SHARING & SAVE ------------------------------------------ */}
          <section className="card flex flex-col gap-3.5 rounded-[16px] border border-line bg-surface p-4 shadow-2xs sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div className="min-w-0">
              <Checkbox
                label="Send to Parents"
                checked={shareWithParents}
                disabled={isSaving || isReadOnly}
                onChange={(event) => setShareWithParents(event.target.checked)}
              />
              <p className="mt-0.5 pl-6 text-2xs font-medium text-ink-3">
                Share this daily report with parents through the parent portal.
              </p>
            </div>

            {!isReadOnly && (
              <div className="flex shrink-0 items-center gap-2 w-full sm:w-auto">
                <Button
                  variant="ghost"
                  disabled={isSaving}
                  onClick={() => navigate('/teacher/daily-report')}
                  className="flex-1 sm:flex-initial"
                >
                  Cancel
                </Button>
                <Button
                  leftIcon={<Send className="h-4 w-4" />}
                  isLoading={isSaving}
                  disabled={
                    !isDirty ||
                    (topic.trim().length === 0 && !report) ||
                    (isNotYetTaught && !isWithdrawing)
                  }
                  onClick={() => void save()}
                  className="flex-1 sm:flex-initial"
                >
                  {isSaving ? 'Saving…' : isWithdrawing ? 'Withdraw Report' : 'Save Report'}
                </Button>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

/** The tints an attendance tile may wear. Literal, so Tailwind can see them. */
const TILE_TONE = {
  emerald: 'border-emerald-500/25 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  rose: 'border-rose-500/25 bg-rose-500/15 text-rose-600 dark:text-rose-400',
  violet: 'border-violet-500/25 bg-violet-500/15 text-violet-600 dark:text-violet-400',
} as const;

interface AttendanceTileProps {
  label: string;
  value: number;
  icon?: React.ReactNode;
  tone?: keyof typeof TILE_TONE;
}

/** One figure off the register. Read-only here — this screen never takes it. */
function AttendanceTile({ label, value, icon, tone }: AttendanceTileProps) {
  return (
    <div className="flex items-center gap-2.5 rounded-field border border-line bg-surface-2 p-2.5">
      {icon && tone && (
        <span
          className={cn('grid h-8 w-8 shrink-0 place-items-center rounded-[10px] border', TILE_TONE[tone])}
          aria-hidden
        >
          {icon}
        </span>
      )}
      <div className="min-w-0">
        <p className="truncate text-2xs font-bold text-ink-3">{label}</p>
        <p className="text-lg font-black leading-tight tabular-nums text-ink">{value}</p>
      </div>
    </div>
  );
}
